import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useImpersonation } from "@/hooks/useImpersonation";
import { Loader2, ShieldAlert } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getPendingDeviceAuth, clearPendingDeviceAuth } from "@/lib/deviceAuthPending";
import { Button } from "@/components/ui/button";

interface Props {
  children: React.ReactNode;
  requiredRole?: "user" | "admin";
}

function FullScreenSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function CheckUnavailable({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <div className="max-w-sm text-center space-y-4">
        <div className="mx-auto p-3 rounded-full bg-primary/10 w-fit">
          <ShieldAlert className="h-7 w-7 text-primary" />
        </div>
        <h1 className="text-lg font-semibold text-foreground">We couldn't complete the security check</h1>
        <p className="text-sm text-muted-foreground">
          Your account is protected until the check completes. Please check your connection and try again.
        </p>
        <Button className="w-full" onClick={onRetry}>Try again</Button>
      </div>
    </div>
  );
}

/**
 * Single ordered gatekeeper for every protected area.
 *
 * Nothing behind it renders until EVERY entry check has a definite answer, so a
 * dashboard can never appear and then bounce to a verification screen. The order is:
 * signed in -> access level known -> device approved -> sign-in code passed ->
 * email verified. Staff two-factor is enforced inside the admin area by AdminMfaGate,
 * which only mounts once these checks pass.
 */
export default function ProtectedRoute({ children, requiredRole }: Props) {
  const { user, loading, roles, rolesResolved } = useAuth();
  const { isImpersonating } = useImpersonation();
  const location = useLocation();

  // When impersonating, admin needs access to user routes — skip all user gates
  const skipUserGates = isImpersonating && requiredRole === "user";
  const userGatesEnabled = !!user?.id && requiredRole === "user" && !skipUserGates;

  // Email verification state
  const profileQuery = useQuery({
    queryKey: ["protected_route_profile", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_profiles" as any)
        .select("email_verified")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as { email_verified: boolean | null } | null;
    },
    enabled: userGatesEnabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: 2,
  });

  // Device authorization is tracked PER DEVICE: only the browser that triggered the
  // "authorize this device" email holds a pending marker, so signing in elsewhere
  // never gates this session.
  const pendingAuthId = getPendingDeviceAuth(user?.id);
  const deviceQuery = useQuery({
    queryKey: ["device_auth_pending", user?.id, pendingAuthId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("login-authorization", {
        body: { action: "check-status", user_id: user!.id, authorization_id: pendingAuthId },
      });
      if (error) throw error;
      const status = (data as any)?.status ?? "unknown";
      if (status !== "pending") clearPendingDeviceAuth(user!.id);
      return status as string;
    },
    enabled: !!user?.id && !!pendingAuthId && !skipUserGates,
    staleTime: 10_000,
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
    retry: 2,
  });

  // Login second-factor state — held on the server and bound to this auth session,
  // so it cannot be skipped by opening an address directly or in another tab.
  const loginGateQuery = useQuery({
    queryKey: ["login_gate_status", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("withdrawal-security", {
        body: { action: "login-gate-status" },
      });
      if (error) throw error;
      return data as {
        requires_mfa: boolean;
        methods: string[];
        satisfied: boolean;
        frozen?: boolean;
      };
    },
    enabled: userGatesEnabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: 2,
  });

  // Staff directory membership: an active staff role grants admin-area entry even
  // when the legacy account-role list doesn't contain "admin".
  const staffEnabled = !!user?.id && requiredRole === "admin" && rolesResolved && !roles.includes("admin");
  const staffQuery = useQuery({
    queryKey: ["protected_route_staff", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_user_roles")
        .select("id")
        .eq("user_id", user!.id)
        .eq("status", "active")
        .limit(1);
      if (error) throw error;
      return (data?.length ?? 0) > 0;
    },
    enabled: staffEnabled,
    staleTime: 60_000,
    retry: 2,
  });

  // ── 1. Session ──
  if (loading) return <FullScreenSpinner />;

  const currentPath = location.pathname + location.search;
  const redirectParam =
    currentPath && currentPath !== "/" ? `?redirect=${encodeURIComponent(currentPath)}` : "";
  const loginPath = requiredRole === "admin" ? "/admin/login" : "/login";

  if (!user) return <Navigate to={`${loginPath}${redirectParam}`} replace />;

  // While impersonating, the session belongs to the target user — admin routes
  // are not reachable until the admin returns to their own session.
  if (requiredRole === "admin" && isImpersonating) {
    return <Navigate to="/app/dashboard" replace />;
  }

  // ── 2. Access level — wait until it is actually known ──
  if (requiredRole && !rolesResolved) return <FullScreenSpinner />;

  if (requiredRole === "admin") {
    if (!roles.includes("admin")) {
      if (staffQuery.isPending || staffQuery.isFetching) return <FullScreenSpinner />;
      if (staffQuery.isError) return <CheckUnavailable onRetry={() => staffQuery.refetch()} />;
      if (!staffQuery.data) return <Navigate to={`${loginPath}${redirectParam}`} replace />;
    }
  } else if (requiredRole && !roles.includes(requiredRole) && !skipUserGates) {
    return <Navigate to={`${loginPath}${redirectParam}`} replace />;
  }

  // ── 3. Device authorization for this browser ──
  if (pendingAuthId && !skipUserGates) {
    if (deviceQuery.isPending) return <FullScreenSpinner />;
    if (deviceQuery.isError) return <CheckUnavailable onRetry={() => deviceQuery.refetch()} />;
    if (deviceQuery.data === "pending" && location.pathname !== "/pending-device-authorization") {
      return <Navigate to={`/pending-device-authorization${redirectParam}`} replace />;
    }
  }

  // ── 4. Sign-in code step and 5. email verification (user areas only) ──
  if (userGatesEnabled) {
    if (loginGateQuery.isPending || profileQuery.isPending) return <FullScreenSpinner />;
    if (loginGateQuery.isError || profileQuery.isError) {
      return (
        <CheckUnavailable
          onRetry={() => {
            void loginGateQuery.refetch();
            void profileQuery.refetch();
          }}
        />
      );
    }

    if (loginGateQuery.data && !loginGateQuery.data.satisfied) {
      return <Navigate to={`/login${redirectParam}`} replace />;
    }

    // ── 6. Account freeze — nothing behind this renders while it stands ──
    if (loginGateQuery.data?.frozen && location.pathname !== "/account-frozen") {
      return <Navigate to="/account-frozen" replace />;
    }


    // No profile row yet (fresh signup mid-provisioning) — don't trap the person.
    if (profileQuery.data && profileQuery.data.email_verified !== true && location.pathname !== "/verify-email") {
      return <Navigate to={`/verify-email${redirectParam}`} replace />;
    }
  }

  return <>{children}</>;
}
